import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    getTestCaseById,
    createTestCase,
    editTestCase,
    deleteTestCase,
} from "../utils/methods";
import { QuillEditor, UploadImage, InputComp } from "../components";
import { strings } from "../utils/strings";
import UserIcon from "../components/Icons/userIcon";
import Button from "../components/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import useFetchOptions from "../hooks/useFetchOptions";
import { toast } from "react-toastify";

const CaseStudiesEdit = () => {
    const { id: currentId } = useParams();
    const navigate = useNavigate();
    const [testCaseData, setTestCaseData] = useState({});
    const [name, setName] = useState("");
    const [type, setType] = useState("");
    const [title, setTitle] = useState("");
    const [disable, setDisable] = useState("");
    const [testerImageUrl, setTesterImageUrl] = useState("");
    const [testCaseImageUrl, setTestCaseImageUrl] = useState("");
    const [testerImageFile, setTesterImageFile] = useState(null);
    const [testCaseImageFile, setTestCaseImageFile] = useState(null);

    const { bloggers = [], blogTypes = [], loading, error } = useFetchOptions();

    useEffect(() => {
        const loadTestCaseData = async () => {
            if (currentId !== "new") {
                try {
                    const testCase = await getTestCaseById(currentId);
                    setTestCaseData(testCase);
                    setName(testCase.testerName || "");
                    setType(testCase.type || "");
                    setTitle(testCase.title || "New Case Study");
                    setTestCaseImageUrl(testCase.testCaseImage || "");
                    setTesterImageUrl(testCase.testerImage || "");
                } catch (error) {
                    console.error("Error loading test case:", error);
                }
            }
        };
        loadTestCaseData();
    }, [currentId]);

    const handleTitleChange = (key, newTitle) => {
        setTitle(newTitle);
        setTestCaseData((prevData) => ({ ...prevData, [key]: newTitle }));
    };

    const handleInputChange = (field, value) => {
        setTestCaseData((prevData) => ({ ...prevData, [field]: value }));
    };

    const handleDescChange = (key, newValue) => {
        setTestCaseData((prevData) => ({ ...prevData, [key]: newValue }));
    };

    const handleOptionChange = (key, newValue) => {
        setTestCaseData((prevData) => ({
            ...prevData,
            [key]: newValue || "",
        }));

        // Update corresponding state if necessary
        switch (key) {
            case "name":
                setName(newValue || "");
                break;
            case "type":
                setType(newValue || "");
                break;
            default:
                break;
        }
    };

    const handleImageUpload = (url, file) => {
        setTestCaseImageUrl(url);
        setTestCaseImageFile(file);
        setTestCaseData((prevData) => ({ ...prevData, testCaseImage: url }));
    };

    const handleTesterImageUpload = (url, file) => {
        setTesterImageUrl(url);
        setTesterImageFile(file);
        setTestCaseData((prevData) => ({ ...prevData, testerImage: url }));
    };

    const formatTitleToId = (title) => {
        return title.trim().replace(/\s+/g, "-").toLowerCase();
    };

    const handleSave = async () => {
        const fieldsToValidate = [
            {
                key: "title",
                value: testCaseData.title,
                label: strings["inputTitle"],
                regex: null,
            },
            {
                key: "desc",
                value: testCaseData.desc,
                label: strings["inputDesc"],
                regex: null,
            },
            {
                key: "type",
                value: type,
                label: strings["inputCategory"],
                regex: null,
            },
            { key: "name", value: name, label: strings["inputTester"], regex: null },
            {
                key: "testCaseImage",
                value: testCaseData.testCaseImage,
                label: strings["testCaseImage"],
                regex: null,
            },
            {
                key: "testerImage",
                value: testCaseData.testerImage,
                label: strings["testerImage"],
                regex: null,
            },
            {
                key: "template",
                value: testCaseData.template,
                label: "Test Case Content",
                regex: null,
            },
        ];

        let isFormValid = true;

        fieldsToValidate.forEach(({ key, value, label, regex }) => {
            if (!value || (regex && !regex.test("should ", () => { })(value))) {
                isFormValid = false;
                toast.error(`${label} is required and must be valid.`);
            }
        });

        if (!isFormValid) return;

        try {
            const newTestCaseId = formatTitleToId(title);
            setDisable(true);

            if (currentId === "new") {
                await createTestCase({
                    ...testCaseData,
                    title,
                    type,
                    testerName: name,
                    testCaseImage: testCaseImageUrl,
                    testerImage: testerImageUrl,
                    createdAt: new Date().toISOString()
                }, newTestCaseId); // Add newTestCaseId here
            } else if (currentId !== newTestCaseId) {
                await createTestCase({
                    ...testCaseData,
                    title,
                    type,
                    testerName: name,
                    testCaseImage: testCaseImageUrl,
                    testerImage: testerImageUrl,
                    updatedAt: new Date().toISOString()
                }, newTestCaseId);
                await deleteTestCase(currentId);
            } else {
                await editTestCase(currentId, {
                    ...testCaseData,
                    updatedAt: new Date().toISOString()
                });
            }
            navigate('/caseStudies');
        } catch (error) {
            setDisable(false);
            console.error("Error saving test case:", error);
            toast.error("Failed to save test case. Please try again.");
        }
    };
    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error loading options: {error.message}</p>;
    return (
        <div className="bg-border w-full h-full overflow-scroll xl:px-[212px] lg:px-[100px] md:px-[60px] pt-12 pb-[60px]">
            <p className="text-[36px] font-semibold">
                {currentId === "new"
                    ? `Add Test Case ${testCaseData?.title || "New"}`
                    : `Edit Test Case ${testCaseData?.title}`}
            </p>

            {/* Title */}

            <InputComp
                label={strings["inputTitle"]}
                placeHolder={strings["titlePlaceholder"]}
                type="text"
                value={title}
                handleChange={handleTitleChange}
                inputKey="title"
                labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
                classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
            />

            {/* Description */}
            <InputComp
                label={strings["inputDesc"]}
                placeHolder={strings["descriptionPlaceholder"]}
                type="text"
                value={testCaseData?.desc || ""}
                handleChange={handleDescChange}
                inputKey="desc"
                labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
                classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
            />

            {/* Type Selector */}
            <div className="mt-5">
                <div className="mb-[6px]">
                    <label className="text-sm font-medium text-color-1">
                        {strings["inputCategory"]}
                    </label>
                </div>
                <Autocomplete
                    className="w-full"
                    options={blogTypes}
                    getOptionLabel={(option) => option || ""}
                    value={type || null}
                    onChange={(event, newValue) => handleOptionChange("type", newValue)}
                    disableClearable
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Select"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    height: 44,
                                    padding: 0,
                                    paddingLeft: 1,
                                    borderRadius: "8px",
                                    backgroundColor: "white",
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiInputBase-input": {
                                    height: "100%",
                                    padding: "0 12px",
                                    textAlign: "start",
                                    boxSizing: "border-box",
                                },
                            }}
                        />
                    )}
                />
            </div>

            {/* Testers Name Selector */}
            <div className="mt-5">
                <div className="mb-[6px]">
                    <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
                        {strings["inputTester"]}
                    </label>
                </div>
                <Autocomplete
                    className="w-full"
                    options={bloggers}
                    getOptionLabel={(option) => option || ""}
                    value={name || null}
                    onChange={(event, newValue) => handleOptionChange("name", newValue)}
                    disableClearable
                    renderOption={(props, option) => (
                        <li {...props} className="flex items-center gap-x-2">
                            <UserIcon />
                            <div className="pt-1">{option}</div>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Select"
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    height: 44,
                                    padding: 0,
                                    paddingLeft: 1,
                                    borderRadius: "8px",
                                    backgroundColor: "white",
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiInputBase-input": {
                                    height: "100%",
                                    padding: "0 12px",
                                    textAlign: "start",
                                    boxSizing: "border-box",
                                },
                            }}
                        />
                    )}
                />
            </div>

            <div className="mt-[20px]">
                <div className="mb-[6px]">
                    <label className="text-sm font-medium pb-[6px] mb-2 text-color-1 mt-[20px]">
                        Case Study Image
                    </label>
                </div>

                {/* Test Case Image */}
                <UploadImage
                    onImageUpload={handleImageUpload}
                    testCaseId={currentId === "new" ? null : currentId}
                    imageType="testCaseImages"
                    initialImageUrl={testCaseData?.testCaseImage}
                />
            </div>

            <div className="mt-[20px]">
                <div className="mb-[6px]">
                    <label className="text-sm font-medium pb-[6px] mb-2 text-color-1">
                        Case Study Writer Image
                    </label>
                </div>

                {/* Tester Image */}
                <UploadImage
                    onImageUpload={handleTesterImageUpload}
                    testCaseId={currentId === "new" ? null : currentId}
                    imageType="caseStudyImages"
                    initialImageUrl={testCaseData?.testerImage}
                />
            </div>

            {/* HTML Editor */}
            <div className="my-6 bg-white ">
                <QuillEditor
                    value={testCaseData?.template || ""}
                    onChange={(content) => handleInputChange("template", content)}
                />
            </div>

            {/* Upload Button */}

            <div className="flex justify-center w-full">
                <Button
                    classes={`!w-full justify-center ${disable ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    onClick={disable ? null : handleSave}
                    disabled={disable}
                >
                    {strings["submitButton"]}
                </Button>
            </div>
        </div>
    );
};

export default CaseStudiesEdit;
